import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Spinner } from '../ui/spinner';
import { Alert, AlertDescription } from '../ui/alert';
import { ToggleGroup, ToggleItem } from '../ui/toggle';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { ChevronRight, ChevronDown, Search, X } from 'lucide-react';
import { livechatMiningAPI } from '../../services/api';
import { useBrand } from '../../context/BrandContext';
import { BACKOFFICE_PLAYER_URL, livechatArchiveThreadUrl } from '../flagged_chats/ThreadRow';
import PatternExportButtons from './PatternExportButtons';
import PatternTrendChart from './PatternTrendChart';

const THREADS_PAGE_SIZE = 25;
const PATTERNS_PAGE_SIZE = 10;
const COMPOSITION_PAGE_SIZE = 10;
const DEFAULT_RANGE_DAYS = 7;
// How the page heading names the brand's chat source. Which source a brand uses comes from the
// backend (brand info's chat_source, i.e. the brand config's livechat_mining.config.data_source)
// — the same setting that picks which tables are queried — never from the brand key, so a new
// brand on Pulse needs no change here.
const CHAT_SOURCE_LABELS = {
  livechat: { title: 'Livechat Mining', support: 'live-chat support' },
  pulse: { title: 'Pulse Mining', support: 'Pulse support' },
};
const chatSourceLabels = (chatSource) => CHAT_SOURCE_LABELS[chatSource] || CHAT_SOURCE_LABELS.livechat;

const DATE_RANGE_PRESETS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const formatState = (value, fallback) => value || fallback;

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const toDateInputValue = (iso) => (iso ? iso.slice(0, 10) : '');

const rangeForLastDays = (anchorIso, days) => {
  const to = new Date(anchorIso);
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - days);
  return { from: from.toISOString(), to: to.toISOString() };
};

const LIFECYCLE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'rdp', label: 'RDP' },
  { value: 'welcome_journey', label: 'Welcome Journey' },
  { value: 'nrc', label: 'NRC' },
  { value: 'unknown', label: 'Not yet active' },
];

const ACTIVITY_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'dormant', label: 'Dormant' },
  { value: 'churned', label: 'Churned' },
  { value: 'unknown', label: 'Not yet active' },
];

const SORT_OPTIONS = [
  { value: 'threads', label: 'Conversations' },
  { value: 'users', label: 'Unique users' },
];

// -- Section / stat chrome --------------------------------------------------

const SectionHeader = ({ number, title, note }) => (
  <div className="flex items-baseline gap-3 border-b border-white/10 pb-2 mb-4">
    <span className="font-mono text-xs font-bold text-cta">{number}</span>
    <h3 className="text-lg font-bold text-ink">{title}</h3>
    {note && <span className="text-xs text-ink-dim ml-auto">{note}</span>}
  </div>
);

const StatTile = ({ label, value, first }) => (
  <div className={`px-5 py-3 ${first ? '' : 'border-l border-white/10'}`}>
    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">{label}</div>
    <div className="text-lg font-bold text-ink mt-0.5">{value}</div>
  </div>
);

// Dropdown filter for a many-valued dimension (signal type/topic) — a ToggleGroup would be
// too wide once there are more than a handful of distinct values, unlike the small
// enumerated lifecycle/activity/actionability sets.
const FilterSelect = ({ label, value, onChange, options }) => (
  <div className="flex items-center gap-1.5">
    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const PaginationFooter = ({ total, page, totalPages, onPageChange }) => {
  if (total === 0) return null;
  return (
    <div className="flex items-center justify-between mt-3">
      <span className="text-[11px] text-ink-dim">{total} total</span>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>← Prev</Button>
        <span className="text-[11px] text-ink-dim">{page} / {totalPages}</span>
        <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>Next →</Button>
      </div>
    </div>
  );
};

// -- Date range filter --------------------------------------------------------

const DateRangeFilter = ({ range, bounds, onChange }) => {
  if (!range || !bounds?.max_start_at) return null;
  const minDate = toDateInputValue(bounds.min_start_at);
  const maxDate = toDateInputValue(bounds.max_start_at);

  const handleFromChange = (e) => {
    if (!e.target.value) return;
    onChange({ from: `${e.target.value}T00:00:00.000Z`, to: range.to });
  };

  const handleToChange = (e) => {
    if (!e.target.value) return;
    onChange({ from: range.from, to: `${e.target.value}T23:59:59.999Z` });
  };

  return (
    <div className="mt-4 flex items-center gap-2 flex-wrap">
      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">Chats active</span>
      <Input
        type="date" value={toDateInputValue(range.from)} min={minDate} max={maxDate}
        onChange={handleFromChange} className="h-8 w-auto text-xs"
      />
      <span className="text-xs text-ink-dim">to</span>
      <Input
        type="date" value={toDateInputValue(range.to)} min={minDate} max={maxDate}
        onChange={handleToChange} className="h-8 w-auto text-xs"
      />
      <div className="flex items-center gap-1 pl-2 border-l border-white/10">
        {DATE_RANGE_PRESETS.map((p) => (
          <Button key={p.days} variant="ghost" size="sm" onClick={() => onChange(rangeForLastDays(bounds.max_start_at, p.days))}>
            {p.label}
          </Button>
        ))}
        <Button variant="ghost" size="sm" onClick={() => onChange({ from: bounds.min_start_at, to: bounds.max_start_at })}>
          All time
        </Button>
      </div>
    </div>
  );
};

// -- Ranked (bar-cell) tables ------------------------------------------------

const RankedTable = ({ title, labelHeader, rows, getLabel, getValue, getKey, totalThreads }) => {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [rows]);

  const max = rows.reduce((m, r) => Math.max(m, getValue(r)), 0);
  const totalPages = Math.max(1, Math.ceil(rows.length / COMPOSITION_PAGE_SIZE));
  const pagedRows = rows.slice((page - 1) * COMPOSITION_PAGE_SIZE, page * COMPOSITION_PAGE_SIZE);

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="pt-0">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead>{labelHeader}</TableHead>
              <TableHead className="text-center w-28">Conversations</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedRows.length === 0 ? (
              <TableRow><TableCell colSpan={2} className="text-center py-6 text-ink-dim">No data.</TableCell></TableRow>
            ) : pagedRows.map((row) => {
              const value = getValue(row);
              const pct = max > 0 ? Math.round((value / max) * 100) : 0;
              const sharePct = totalThreads > 0 ? Math.round((value / totalThreads) * 100) : 0;
              return (
                <TableRow key={getKey(row)}>
                  <TableCell className="relative">
                    <div className="absolute inset-y-1.5 left-0 rounded bg-cta/15" style={{ width: `${pct}%` }} />
                    <span className="relative z-10">{getLabel(row)}</span>
                  </TableCell>
                  <TableCell className="text-center relative z-10 font-mono text-xs">
                    {value} <span className="text-ink-mute">({sharePct}%)</span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <PaginationFooter total={rows.length} page={page} totalPages={totalPages} onPageChange={setPage} />
      </CardContent>
    </Card>
  );
};

const LifecycleCellTable = ({ rows, totalThreads }) => {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [rows]);

  const max = rows.reduce((m, r) => Math.max(m, r.thread_count), 0);
  const totalPages = Math.max(1, Math.ceil(rows.length / COMPOSITION_PAGE_SIZE));
  const pagedRows = rows.slice((page - 1) * COMPOSITION_PAGE_SIZE, page * COMPOSITION_PAGE_SIZE);

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">By lifecycle cell</CardTitle></CardHeader>
      <CardContent className="pt-0">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead>Lifecycle state</TableHead>
              <TableHead>Activity state</TableHead>
              <TableHead className="text-center w-28">Conversations</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedRows.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center py-6 text-ink-dim">No data.</TableCell></TableRow>
            ) : pagedRows.map((row, idx) => {
              const pct = max > 0 ? Math.round((row.thread_count / max) * 100) : 0;
              const sharePct = totalThreads > 0 ? Math.round((row.thread_count / totalThreads) * 100) : 0;
              return (
                <TableRow key={`${row.lifecycle_state}-${row.activity_state}-${idx}`}>
                  <TableCell className="relative">
                    <div className="absolute inset-y-1.5 left-0 rounded bg-cta/15" style={{ width: `${pct}%` }} />
                    <span className="relative z-10">{formatState(row.lifecycle_state, '—')}</span>
                  </TableCell>
                  <TableCell>{formatState(row.activity_state, 'not yet active')}</TableCell>
                  <TableCell className="text-center font-mono text-xs">
                    {row.thread_count} <span className="text-ink-mute">({sharePct}%)</span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <PaginationFooter total={rows.length} page={page} totalPages={totalPages} onPageChange={setPage} />
      </CardContent>
    </Card>
  );
};

// -- Pattern cards ------------------------------------------------------------

const ThreadDetailField = ({ label, value, mono, href }) => (
  <div className="min-w-0">
    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-mute mb-1">{label}</div>
    {href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`block truncate text-cta hover:underline ${mono ? 'font-mono' : ''}`}
        title={value}
      >
        {value}
      </a>
    ) : (
      <div className={`text-ink truncate ${mono ? 'font-mono' : ''}`} title={value}>{value}</div>
    )}
  </div>
);

const ConversationBubble = ({ message }) => {
  const isCustomer = message.sent_by === 'customer';
  return (
    <div className={`flex ${isCustomer ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-lg border px-3 py-2 ${
          isCustomer
            ? 'rounded-tr-sm bg-brand-blue/10 border-brand-blue/20'
            : 'rounded-tl-sm bg-brand-green/10 border-brand-green/20'
        }`}
      >
        <div className="flex items-center justify-between gap-3 mb-1">
          <span className="text-xs font-bold text-ink-dim">{isCustomer ? 'Customer' : 'Agent'}</span>
          <span className="text-[10px] text-ink-mute whitespace-nowrap">{formatDate(message.sent_at)}</span>
        </div>
        <p className="text-sm whitespace-pre-wrap">{message.text}</p>
      </div>
    </div>
  );
};

const ConversationDialog = ({ threadId }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || messages !== null) return undefined;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError('');
        const resp = await livechatMiningAPI.getThreadConversation(threadId);
        if (cancelled) return;
        setMessages(resp.data.messages || []);
      } catch {
        if (!cancelled) setError('Failed to load conversation.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, messages, threadId]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">Show conversation</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-sm">
            Conversation ID: <span className="font-mono">{threadId}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {loading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : error ? (
            <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>
          ) : !messages || messages.length === 0 ? (
            <div className="text-sm text-ink-dim text-center py-8">No messages.</div>
          ) : messages.map((m, i) => <ConversationBubble key={i} message={m} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ThreadRow = ({ thread, isExpanded, onToggle }) => {
  const { activeBrand } = useBrand();
  const backofficePlayerUrl = BACKOFFICE_PLAYER_URL[activeBrand] || BACKOFFICE_PLAYER_URL.bitstarz;
  return (
    <div>
      <button
        type="button"
        onClick={() => onToggle(thread.thread_id)}
        className="w-full text-left py-2.5 flex items-start gap-2.5 hover:bg-surface-2/50 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 mt-0.5 text-cta shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 mt-0.5 text-ink-dim shrink-0" />
        )}
        <span className="flex-1 min-w-0 text-sm italic text-ink-dim truncate">
          {thread.representative_quote || '—'}
        </span>
        <span className="text-xs text-ink-dim whitespace-nowrap shrink-0">{formatDate(thread.start_at)}</span>
      </button>
      {isExpanded && (
        <div className="pb-3 pl-6 pr-2 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs">
            <ThreadDetailField
              label="Conversation ID" value={thread.thread_id} mono
              href={livechatArchiveThreadUrl(activeBrand, thread.thread_id)}
            />
            <ThreadDetailField
              label="Email" value={formatState(thread.email, '—')}
              href={thread.user_id != null ? `${backofficePlayerUrl}/${encodeURIComponent(thread.user_id)}` : undefined}
            />
            <ThreadDetailField label="Lifecycle" value={formatState(thread.lifecycle_state, '—')} />
            <ThreadDetailField label="Activity" value={formatState(thread.activity_state, 'not yet active')} />
            <ThreadDetailField label="Actionability" value={thread.actionability} />
          </div>
          <ConversationDialog threadId={thread.thread_id} />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-mute mb-1">Representative quote</div>
            <div className="text-sm italic text-ink">{thread.representative_quote || '—'}</div>
          </div>
          {thread.classification_rationale && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-mute mb-1">Rationale</div>
              <div className="text-xs text-ink-dim">{thread.classification_rationale}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PatternThreadsTable = ({ threads, total, page, onPageChange }) => {
  const [expandedThreadId, setExpandedThreadId] = useState(null);
  const totalPages = Math.max(1, Math.ceil(total / THREADS_PAGE_SIZE));

  const toggleThread = useCallback((threadId) => {
    setExpandedThreadId((prev) => (prev === threadId ? null : threadId));
  }, []);

  return (
    <>
      <div className="divide-y divide-white/5">
        {threads.length === 0 ? (
          <div className="text-center py-4 text-sm text-ink-dim">No conversations.</div>
        ) : threads.map((t) => (
          <ThreadRow key={t.thread_id} thread={t} isExpanded={expandedThreadId === t.thread_id} onToggle={toggleThread} />
        ))}
      </div>
      {total > 0 && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-[11px] text-ink-dim">{total} total</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>← Prev</Button>
            <span className="text-[11px] text-ink-dim">{page} / {totalPages}</span>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>Next →</Button>
          </div>
        </div>
      )}
    </>
  );
};

// lockedValue is the top-level dropdown's value for this dimension (signal type or topic),
// if any. A click on a DIFFERENT value in the same dimension would silently replace that
// top-level constraint instead of narrowing it further (the in-card filter is a single
// value, not an AND of both) — so those entries render inert rather than clickable. The
// locked value itself, and every entry in the other dimension, stay clickable as before.
const SignalBreakdownColumn = ({ label, type, values, activeFilter, onSelect, emptyLabel, lockedValue }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-mute mb-1.5">{label}</div>
    {values.length === 0 ? (
      <div className="text-sm text-ink-dim">{emptyLabel}</div>
    ) : values.map((v) => {
      const isActive = activeFilter?.type === type && activeFilter?.value === v.value;
      const isLockedOut = lockedValue && v.value !== lockedValue;
      if (isLockedOut) {
        return (
          <div
            key={v.value}
            title={`Locked by the top-level ${type === 'signal' ? 'signal type' : 'topic'} filter — clear it to explore this value instead`}
            className="flex items-center gap-1.5 w-full px-1 -mx-1 py-0.5 text-sm leading-relaxed text-ink-mute/60 cursor-not-allowed"
          >
            <span className="truncate">{v.value}</span>
            <span className="font-mono text-xs text-ink-mute/60 shrink-0">{v.count}</span>
          </div>
        );
      }
      return (
        <button
          key={v.value}
          type="button"
          onClick={() => onSelect(type, v.value)}
          className={`flex items-center gap-1.5 w-full text-left rounded px-1 -mx-1 py-0.5 text-sm leading-relaxed transition-colors hover:bg-surface-2/70 ${isActive ? 'text-cta font-semibold' : 'text-ink'}`}
        >
          <span className="truncate">{v.value}</span>
          <span className="font-mono text-xs text-ink-dim shrink-0">{v.count}</span>
        </button>
      );
    })}
  </div>
);

const PatternSignalBreakdown = ({ signals, loading, activeFilter, onSelect, lockedSignalType, lockedTopic }) => {
  if (loading) return <div className="flex justify-center py-4"><Spinner size="sm" /></div>;
  if (!signals) return null;

  const [primary, ...secondary] = signals.signalTypes;

  return (
    <div className="grid grid-cols-3 gap-4 mb-4 pb-3 border-b border-white/10">
      <SignalBreakdownColumn
        label="Primary signal" type="signal" values={primary ? [primary] : []}
        activeFilter={activeFilter} onSelect={onSelect} emptyLabel="—" lockedValue={lockedSignalType}
      />
      <SignalBreakdownColumn
        label="Secondary" type="signal" values={secondary}
        activeFilter={activeFilter} onSelect={onSelect} emptyLabel="—" lockedValue={lockedSignalType}
      />
      <SignalBreakdownColumn
        label="Topics" type="topic" values={signals.topics}
        activeFilter={activeFilter} onSelect={onSelect} emptyLabel="—" lockedValue={lockedTopic}
      />
    </div>
  );
};

const PatternCard = ({
  group, maxThreads, maxUsers, isExpanded, onToggle, threads, total, page, onPageChange, threadsLoading,
  signals, signalsLoading, threadFilter, onFilterSelect, threadsSortOrder, onThreadsSortOrderChange, drilldownParams,
  lockedSignalType, lockedTopic,
}) => {
  const threadsPct = maxThreads > 0 ? Math.round((group.displayedThreads / maxThreads) * 100) : 0;
  const usersPct = maxUsers > 0 ? Math.round((group.displayedUsers / maxUsers) * 100) : 0;
  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => onToggle(group.pattern)}
        className="w-full text-left p-4 flex items-start gap-3 hover:bg-surface-2/50 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 mt-1 text-cta shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 mt-1 text-ink-dim shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className="font-bold text-ink">{group.pattern}</span>
            <Badge variant={group.confirmed ? 'success' : 'outline'}>
              {group.confirmed ? 'Confirmed' : 'Unconfirmed'}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 flex-1 max-w-xs rounded bg-surface-3 overflow-hidden">
              <div className="h-full bg-cta" style={{ width: `${threadsPct}%` }} />
            </div>
            <span className="font-mono text-xs text-ink-dim shrink-0 w-[136px]">
              {group.displayedThreads} conversations
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <div className="h-1.5 flex-1 max-w-xs rounded bg-surface-3 overflow-hidden">
              <div className="h-full bg-brand-blue" style={{ width: `${usersPct}%` }} />
            </div>
            <span className="font-mono text-xs text-ink-dim shrink-0 w-[104px]">
              {group.displayedUsers} unique users
            </span>
          </div>
        </div>
      </button>
      {isExpanded && (
        <div className="border-t border-white/10 px-4 py-3">
          <PatternTrendChart pattern={group.pattern} params={drilldownParams} />
          <PatternSignalBreakdown
            signals={signals} loading={signalsLoading} activeFilter={threadFilter} onSelect={onFilterSelect}
            lockedSignalType={lockedSignalType} lockedTopic={lockedTopic}
          />
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div className="flex flex-wrap gap-1.5">
              {group.segments.map((s, i) => (
                <span
                  key={`${s.lifecycleState}-${s.activityState}-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-2.5 py-0.5 text-[11px] text-ink-dim"
                >
                  {formatState(s.lifecycleState, '—')} · {formatState(s.activityState, 'not yet active')}
                  <span className="font-mono text-ink">{s.threadCount}t / {s.uniqueUsers}u</span>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">Sort by date</span>
              <ToggleGroup value={threadsSortOrder} onChange={onThreadsSortOrderChange}>
                <ToggleItem value="desc">Newest</ToggleItem>
                <ToggleItem value="asc">Oldest</ToggleItem>
              </ToggleGroup>
            </div>
          </div>
          <div className="flex justify-end mb-3">
            <PatternExportButtons pattern={group.pattern} params={drilldownParams} />
          </div>
          {threadFilter && (
            <div className="flex items-center gap-2 mb-2 text-xs text-ink-dim">
              <span>Filtered by <span className="text-ink font-medium">{threadFilter.value}</span></span>
              <button
                type="button"
                onClick={() => onFilterSelect(threadFilter.type, threadFilter.value)}
                className="text-cta hover:underline"
              >
                Clear
              </button>
            </div>
          )}
          {threadsLoading ? (
            <div className="flex justify-center py-4"><Spinner size="sm" /></div>
          ) : (
            <PatternThreadsTable threads={threads || []} total={total} page={page} onPageChange={onPageChange} />
          )}
        </div>
      )}
    </Card>
  );
};

// -- Page ---------------------------------------------------------------------

const LivechatMiningPage = () => {
  const { activeBrandInfo } = useBrand();
  const sourceLabels = chatSourceLabels(activeBrandInfo?.chat_source);
  const [summary, setSummary] = useState(null);
  const [patternReport, setPatternReport] = useState([]);
  const [housekeeping, setHousekeeping] = useState([]);
  const [housekeepingPage, setHousekeepingPage] = useState(1);
  const [dateBounds, setDateBounds] = useState(null);
  const [range, setRange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [lifecycleFilter, setLifecycleFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');
  const [actionabilityFilter, setActionabilityFilter] = useState('all');
  const [signalTypeFilter, setSignalTypeFilter] = useState('all');
  const [topicFilter, setTopicFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [patternsLoading, setPatternsLoading] = useState(false);
  const [sortBy, setSortBy] = useState('threads');
  const [patternsPage, setPatternsPage] = useState(1);
  const [expandedPattern, setExpandedPattern] = useState(null);
  const [threadsPage, setThreadsPage] = useState(1);
  const [threadsResult, setThreadsResult] = useState({ pattern: null, threads: [], total: 0 });
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [signalsResult, setSignalsResult] = useState({ pattern: null, signalTypes: [], topics: [] });
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [threadFilter, setThreadFilter] = useState(null);
  const [threadsSortOrder, setThreadsSortOrder] = useState('desc');
  const [filteredSegmentsResult, setFilteredSegmentsResult] = useState({ pattern: null, segments: null });

  // Debounce the raw search input so typing doesn't fire a fetch on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Bounds + pattern housekeeping load once; they seed the default "last 7 days of the
  // data" range and don't depend on it themselves.
  useEffect(() => {
    (async () => {
      try {
        setError('');
        const [boundsResp, housekeepingResp] = await Promise.all([
          livechatMiningAPI.getDateBounds(),
          livechatMiningAPI.getPatternHousekeeping(),
        ]);
        const bounds = boundsResp.data;
        setDateBounds(bounds);
        setHousekeeping(housekeepingResp.data.patterns || []);
        setRange(bounds.max_start_at ? rangeForLastDays(bounds.max_start_at, DEFAULT_RANGE_DAYS) : { from: null, to: null });
      } catch {
        setError('Failed to load the signal report.');
        setLoading(false);
      }
    })();
  }, []);

  // Summary re-fetches whenever the active-chats date range changes.
  useEffect(() => {
    if (!range) return undefined;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError('');
        const resp = await livechatMiningAPI.getSummary(range.from, range.to);
        if (cancelled) return;
        setSummary(resp.data);
      } catch {
        if (!cancelled) setError('Failed to load the signal report.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [range]);

  // Pattern report re-fetches whenever the date range, actionability/signal type/topic
  // filter, or search keyword changes — kept separate from Summary so these Patterns-only
  // filters don't reload the Volume & composition tables. None of these are part of the
  // pattern's (lifecycle, activity) segment breakdown, so — unlike the lifecycle/activity
  // toggles — they can't be applied client-side and need their own fetch. Signal
  // type/topic here narrow which patterns show up at all (e.g. "friction" signal for
  // churned users), distinct from the in-card threadFilter which only narrows an already-
  // expanded pattern's own thread list.
  useEffect(() => {
    if (!range) return undefined;
    let cancelled = false;

    (async () => {
      try {
        setPatternsLoading(true);
        setError('');
        const actionability = actionabilityFilter === 'all' ? '' : actionabilityFilter;
        const signalType = signalTypeFilter === 'all' ? '' : signalTypeFilter;
        const topic = topicFilter === 'all' ? '' : topicFilter;
        const resp = await livechatMiningAPI.getPatternReport(range.from, range.to, actionability, signalType, topic, search);
        if (cancelled) return;
        setPatternReport(resp.data.patterns || []);
        setExpandedPattern(null);
        setPatternsPage(1);
      } catch {
        if (!cancelled) setError('Failed to load the signal report.');
      } finally {
        if (!cancelled) setPatternsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [range, actionabilityFilter, signalTypeFilter, topicFilter, search]);

  const togglePattern = useCallback((pattern) => {
    setExpandedPattern((prev) => (prev === pattern ? null : pattern));
    setThreadsPage(1);
    setThreadFilter(null);
    setThreadsSortOrder('desc');
  }, []);

  const handleThreadFilterSelect = useCallback((type, value) => {
    setThreadFilter((prev) => (prev && prev.type === type && prev.value === value ? null : { type, value }));
    setThreadsPage(1);
  }, []);

  const handleThreadsSortOrderChange = useCallback((value) => {
    setThreadsSortOrder(value);
    setThreadsPage(1);
  }, []);

  // Signal type/topic actually applied to a pattern's drill-down (threads/segments/export/
  // trend): the in-card click filter (threadFilter) takes precedence over the top-level
  // dropdown for that one dimension — it's a further narrowing within an already-expanded
  // pattern — otherwise the top-level dropdown applies, so the drill-down stays consistent
  // with why this pattern is even in the filtered list in the first place.
  const topLevelSignalType = signalTypeFilter === 'all' ? '' : signalTypeFilter;
  const topLevelTopic = topicFilter === 'all' ? '' : topicFilter;
  const effectiveSignalType = threadFilter?.type === 'signal' ? threadFilter.value : topLevelSignalType;
  const effectiveTopic = threadFilter?.type === 'topic' ? threadFilter.value : topLevelTopic;

  useEffect(() => {
    if (!expandedPattern || !range) return undefined;
    let cancelled = false;

    (async () => {
      try {
        setThreadsLoading(true);
        const lifecycleState = lifecycleFilter === 'all' ? '' : lifecycleFilter;
        const activityState = activityFilter === 'all' ? '' : activityFilter;
        const actionability = actionabilityFilter === 'all' ? '' : actionabilityFilter;
        const resp = await livechatMiningAPI.getPatternThreads(
          expandedPattern, range.from, range.to, lifecycleState, activityState, actionability,
          effectiveSignalType, effectiveTopic, search, threadsSortOrder, threadsPage, THREADS_PAGE_SIZE,
        );
        if (cancelled) return;
        setThreadsResult({ pattern: expandedPattern, threads: resp.data.threads || [], total: resp.data.total || 0 });
      } catch {
        if (!cancelled) setThreadsResult({ pattern: expandedPattern, threads: [], total: 0 });
      } finally {
        if (!cancelled) setThreadsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [
    expandedPattern, threadsPage, range, effectiveSignalType, effectiveTopic, lifecycleFilter, activityFilter,
    actionabilityFilter, search, threadsSortOrder,
  ]);

  // The pattern's own (lifecycle, activity) segments (from patternReport) have no idea
  // about the in-card signal/topic click-filter (threadFilter) — they're computed before
  // that filter exists. Only when threadFilter is active do we need a fresh, filter-aware
  // segment breakdown so the pills shown match what the thread table below is scoped to;
  // otherwise the existing client-side segments (already correct for lifecycle/activity/
  // actionability/search/top-level signal/topic, since all of those are also applied
  // server-side to patternReport) are used and this fetch is skipped entirely.
  useEffect(() => {
    if (!expandedPattern || !range || !threadFilter) {
      setFilteredSegmentsResult({ pattern: null, segments: null });
      return undefined;
    }
    let cancelled = false;

    (async () => {
      try {
        const lifecycleState = lifecycleFilter === 'all' ? '' : lifecycleFilter;
        const activityState = activityFilter === 'all' ? '' : activityFilter;
        const actionability = actionabilityFilter === 'all' ? '' : actionabilityFilter;
        const resp = await livechatMiningAPI.getPatternSegments(
          expandedPattern, range.from, range.to, lifecycleState, activityState, actionability,
          effectiveSignalType, effectiveTopic, search,
        );
        if (cancelled) return;
        setFilteredSegmentsResult({ pattern: expandedPattern, segments: resp.data.segments || [] });
      } catch {
        if (!cancelled) setFilteredSegmentsResult({ pattern: expandedPattern, segments: [] });
      }
    })();

    return () => { cancelled = true; };
  }, [expandedPattern, range, threadFilter, effectiveSignalType, effectiveTopic, lifecycleFilter, activityFilter, actionabilityFilter, search]);

  // Primary/secondary signal + topic breakdown for the expanded pattern — independent of
  // threadsPage since it summarizes across all of the pattern's threads, not one page. Must
  // stay filtered by the same lifecycle/activity/actionability/search/top-level signal-topic
  // state as the "N threads" figure shown on the card, or the breakdown counts can exceed
  // that total. Deliberately NOT narrowed by the in-card threadFilter click — that would
  // make the very entries the user can click on disappear once one is picked.
  useEffect(() => {
    if (!expandedPattern || !range) return undefined;
    let cancelled = false;

    (async () => {
      try {
        setSignalsLoading(true);
        const lifecycleState = lifecycleFilter === 'all' ? '' : lifecycleFilter;
        const activityState = activityFilter === 'all' ? '' : activityFilter;
        const actionability = actionabilityFilter === 'all' ? '' : actionabilityFilter;
        const resp = await livechatMiningAPI.getPatternSignals(
          expandedPattern, range.from, range.to, lifecycleState, activityState, actionability,
          topLevelSignalType, topLevelTopic, search,
        );
        if (cancelled) return;
        setSignalsResult({
          pattern: expandedPattern,
          signalTypes: (resp.data.signal_types || []).map((s) => ({ value: s.signal_type, count: s.thread_count })),
          topics: (resp.data.topics || []).map((t) => ({ value: t.topic, count: t.thread_count })),
        });
      } catch {
        if (!cancelled) setSignalsResult({ pattern: expandedPattern, signalTypes: [], topics: [] });
      } finally {
        if (!cancelled) setSignalsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [expandedPattern, range, lifecycleFilter, activityFilter, actionabilityFilter, topLevelSignalType, topLevelTopic, search]);

  const patternGroups = useMemo(() => {
    const byPattern = new Map();
    for (const row of patternReport) {
      const existing = byPattern.get(row.pattern);
      const segment = {
        lifecycleState: row.lifecycle_state,
        activityState: row.activity_state,
        threadCount: row.thread_count,
        uniqueUsers: row.unique_users,
      };
      if (existing) {
        byPattern.set(row.pattern, {
          ...existing,
          totalThreads: existing.totalThreads + row.thread_count,
          segments: [...existing.segments, segment],
        });
      } else {
        byPattern.set(row.pattern, {
          pattern: row.pattern,
          confirmed: row.pattern_confirmed,
          totalThreads: row.thread_count,
          // pattern_users is the backend's exact COUNT(DISTINCT user_id) for the whole
          // pattern (same value on every row for this pattern) — not summed from segments,
          // since a player can touch more than one segment (e.g. moved from
          // welcome_journey to rdp between two threads on the same issue) and summing would
          // double-count them.
          totalUniqueUsers: row.pattern_users,
          segments: [segment],
        });
      }
    }
    return Array.from(byPattern.values()).sort((a, b) => b.totalThreads - a.totalThreads);
  }, [patternReport]);

  // Thread counts summed across segments are always exact (a thread belongs to exactly one
  // segment). Unique-user counts summed across segments are an upper bound, not exact, for
  // the same reason noted above — this only matters once a lifecycle/activity filter
  // narrows the view to a subset of segments (the unfiltered pattern-level total above
  // already uses the backend's exact count instead of summing).
  const segmentMatchesFilter = useCallback((segment) => {
    const lifecycleOk = lifecycleFilter === 'all'
      || (lifecycleFilter === 'unknown' ? segment.lifecycleState == null : segment.lifecycleState === lifecycleFilter);
    const activityOk = activityFilter === 'all'
      || (activityFilter === 'unknown' ? segment.activityState == null : segment.activityState === activityFilter);
    return lifecycleOk && activityOk;
  }, [lifecycleFilter, activityFilter]);

  const filteredPatternGroups = useMemo(() => {
    const withDisplayed = lifecycleFilter === 'all' && activityFilter === 'all'
      ? patternGroups.map((g) => ({ ...g, displayedThreads: g.totalThreads, displayedUsers: g.totalUniqueUsers }))
      : patternGroups
        .filter((g) => g.segments.some(segmentMatchesFilter))
        .map((g) => {
          const matching = g.segments.filter(segmentMatchesFilter);
          return {
            ...g,
            segments: matching,
            displayedThreads: matching.reduce((sum, s) => sum + s.threadCount, 0),
            displayedUsers: matching.reduce((sum, s) => sum + s.uniqueUsers, 0),
          };
        });
    const sortKey = sortBy === 'users' ? 'displayedUsers' : 'displayedThreads';
    return [...withDisplayed].sort((a, b) => b[sortKey] - a[sortKey]);
  }, [patternGroups, lifecycleFilter, activityFilter, segmentMatchesFilter, sortBy]);

  const patternsTotalPages = Math.max(1, Math.ceil(filteredPatternGroups.length / PATTERNS_PAGE_SIZE));
  const pagedPatternGroups = filteredPatternGroups.slice(
    (patternsPage - 1) * PATTERNS_PAGE_SIZE,
    patternsPage * PATTERNS_PAGE_SIZE,
  );

  const handleLifecycleFilterChange = useCallback((value) => {
    setLifecycleFilter(value);
    setPatternsPage(1);
    setThreadsPage(1);
  }, []);

  const handleActivityFilterChange = useCallback((value) => {
    setActivityFilter(value);
    setPatternsPage(1);
    setThreadsPage(1);
  }, []);

  const handleActionabilityFilterChange = useCallback((value) => {
    setActionabilityFilter(value);
    setThreadsPage(1);
  }, []);

  const handleSignalTypeFilterChange = useCallback((value) => {
    setSignalTypeFilter(value);
    setThreadsPage(1);
  }, []);

  const handleTopicFilterChange = useCallback((value) => {
    setTopicFilter(value);
    setThreadsPage(1);
  }, []);

  const handleSortByChange = useCallback((value) => {
    setSortBy(value);
    setPatternsPage(1);
  }, []);

  const actionabilityOptions = useMemo(
    () => (summary?.actionability || []).map((a) => a.actionability),
    [summary],
  );

  const signalTypeOptions = useMemo(
    () => (summary?.signal_types || []).map((s) => s.signal_type),
    [summary],
  );

  const topicOptions = useMemo(
    () => (summary?.topics || []).map((t) => t.topic),
    [summary],
  );

  // Same lifecycle/activity/actionability/signal/topic filters applied to a pattern's
  // drill-down (threads fetch effect above), packaged for anything else that needs to match
  // exactly what's currently shown in an expanded card's thread table — the export buttons
  // (plus sort order) and the trend chart.
  const drilldownParams = useMemo(() => ({
    from: range?.from,
    to: range?.to,
    lifecycleState: lifecycleFilter === 'all' ? '' : lifecycleFilter,
    activityState: activityFilter === 'all' ? '' : activityFilter,
    actionability: actionabilityFilter === 'all' ? '' : actionabilityFilter,
    signalType: effectiveSignalType,
    topic: effectiveTopic,
    search,
    sortOrder: threadsSortOrder,
  }), [range, lifecycleFilter, activityFilter, actionabilityFilter, effectiveSignalType, effectiveTopic, search, threadsSortOrder]);

  const maxPatternThreads = filteredPatternGroups.reduce((m, g) => Math.max(m, g.displayedThreads), 0);
  const maxPatternUsers = filteredPatternGroups.reduce((m, g) => Math.max(m, g.displayedUsers), 0);

  const totalThreads = useMemo(
    () => (summary?.lifecycle_cells || []).reduce((sum, r) => sum + r.thread_count, 0),
    [summary],
  );

  const stats = useMemo(() => [
    { label: 'Analyzed conversations', value: totalThreads },
    { label: 'Non-noise conversations', value: summary?.non_noise_threads ?? 0 },
    { label: 'Patterns', value: patternGroups.length },
    { label: 'Unconfirmed patterns', value: housekeeping.length },
    { label: 'Signal types', value: (summary?.signal_types || []).length },
    { label: 'Topics', value: (summary?.topics || []).length },
  ], [summary, patternGroups, housekeeping, totalThreads]);

  const housekeepingTotalPages = Math.max(1, Math.ceil(housekeeping.length / COMPOSITION_PAGE_SIZE));
  const pagedHousekeeping = housekeeping.slice(
    (housekeepingPage - 1) * COMPOSITION_PAGE_SIZE,
    housekeepingPage * COMPOSITION_PAGE_SIZE,
  );

  return (
    <div className="max-w-6xl mx-auto py-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ink">{sourceLabels.title} — Signal Report</h2>
        <p className="text-sm text-ink-dim mt-1">
          Friction, expectation, and departure signal from {sourceLabels.support}, by lifecycle stage.
        </p>
      </div>

      <div className="sticky top-14 z-20 bg-surface pb-4 mb-6 border-b border-white/5">
        {!loading && (
          <div className="flex flex-wrap rounded-lg border border-white/10 bg-surface-1 overflow-hidden">
            {stats.map((s, i) => <StatTile key={s.label} label={s.label} value={s.value} first={i === 0} />)}
          </div>
        )}
        <DateRangeFilter range={range} bounds={dateBounds} onChange={setRange} />
      </div>

      {error && <Alert variant="error" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : (
        <>
          <section className="mb-8">
            <SectionHeader number="01" title="Patterns" note="How many chats report similar stuff" />
            <div className="relative max-w-md mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-mute" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search chats (email, quote, rationale, conversation)…"
                className="pl-9 pr-9 h-9 text-sm"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-mute hover:text-ink"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="mb-3 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <ToggleGroup value={lifecycleFilter} onChange={handleLifecycleFilterChange}>
                  {LIFECYCLE_FILTERS.map((f) => (
                    <ToggleItem key={f.value} value={f.value}>{f.label}</ToggleItem>
                  ))}
                </ToggleGroup>
                <ToggleGroup value={activityFilter} onChange={handleActivityFilterChange}>
                  {ACTIVITY_FILTERS.map((f) => (
                    <ToggleItem key={f.value} value={f.value}>{f.label}</ToggleItem>
                  ))}
                </ToggleGroup>
                <ToggleGroup value={actionabilityFilter} onChange={handleActionabilityFilterChange}>
                  <ToggleItem value="all">All</ToggleItem>
                  {actionabilityOptions.map((a) => (
                    <ToggleItem key={a} value={a}>{capitalize(a)}</ToggleItem>
                  ))}
                </ToggleGroup>
                <div className="flex items-center gap-3 pl-3 border-l border-white/10">
                  <FilterSelect
                    label="Signal type" value={signalTypeFilter} onChange={handleSignalTypeFilterChange}
                    options={signalTypeOptions}
                  />
                  <FilterSelect label="Topic" value={topicFilter} onChange={handleTopicFilterChange} options={topicOptions} />
                </div>
                <div className="flex items-center gap-2 pl-3 border-l border-white/10">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">Sort by</span>
                  <ToggleGroup value={sortBy} onChange={handleSortByChange}>
                    {SORT_OPTIONS.map((o) => (
                      <ToggleItem key={o.value} value={o.value}>{o.label}</ToggleItem>
                    ))}
                  </ToggleGroup>
                </div>
              </div>
              <span className="text-xs text-ink-dim">
                {filteredPatternGroups.length} of {patternGroups.length} patterns
                {lifecycleFilter !== 'all' || activityFilter !== 'all' || actionabilityFilter !== 'all'
                  || signalTypeFilter !== 'all' || topicFilter !== 'all' || search
                  ? ' match this filter' : ''}
              </span>
            </div>
            <div className="flex flex-col gap-3">
              {patternsLoading ? (
                <div className="flex justify-center py-8"><Spinner /></div>
              ) : pagedPatternGroups.length === 0 ? (
                <Card><CardContent className="p-6 text-center text-ink-dim">No patterns match this filter.</CardContent></Card>
              ) : pagedPatternGroups.map((group) => {
                const isExpanded = expandedPattern === group.pattern;
                const hasResult = threadsResult.pattern === group.pattern;
                const hasSignals = signalsResult.pattern === group.pattern;
                const hasFilteredSegments = threadFilter
                  && filteredSegmentsResult.pattern === group.pattern
                  && filteredSegmentsResult.segments;
                const displayGroup = hasFilteredSegments
                  ? {
                    ...group,
                    segments: filteredSegmentsResult.segments.map((s) => ({
                      lifecycleState: s.lifecycle_state,
                      activityState: s.activity_state,
                      threadCount: s.thread_count,
                      uniqueUsers: s.unique_users,
                    })),
                    displayedThreads: filteredSegmentsResult.segments.reduce((sum, s) => sum + s.thread_count, 0),
                    displayedUsers: filteredSegmentsResult.segments.reduce((sum, s) => sum + s.unique_users, 0),
                  }
                  : group;
                return (
                  <PatternCard
                    key={group.pattern}
                    group={displayGroup}
                    maxThreads={maxPatternThreads}
                    maxUsers={maxPatternUsers}
                    isExpanded={isExpanded}
                    onToggle={togglePattern}
                    threads={hasResult ? threadsResult.threads : []}
                    total={hasResult ? threadsResult.total : 0}
                    page={threadsPage}
                    onPageChange={setThreadsPage}
                    threadsLoading={threadsLoading && isExpanded}
                    signals={hasSignals ? signalsResult : null}
                    signalsLoading={signalsLoading && isExpanded}
                    threadFilter={isExpanded ? threadFilter : null}
                    onFilterSelect={handleThreadFilterSelect}
                    threadsSortOrder={threadsSortOrder}
                    onThreadsSortOrderChange={handleThreadsSortOrderChange}
                    drilldownParams={drilldownParams}
                    lockedSignalType={topLevelSignalType}
                    lockedTopic={topLevelTopic}
                  />
                );
              })}
            </div>
            {!patternsLoading && filteredPatternGroups.length > 0 && (
              <div className="flex items-center justify-between mt-3">
                <span className="text-[11px] text-ink-dim">{filteredPatternGroups.length} total</span>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" disabled={patternsPage <= 1} onClick={() => setPatternsPage((p) => p - 1)}>← Prev</Button>
                  <span className="text-[11px] text-ink-dim">{patternsPage} / {patternsTotalPages}</span>
                  <Button variant="ghost" size="sm" disabled={patternsPage >= patternsTotalPages} onClick={() => setPatternsPage((p) => p + 1)}>Next →</Button>
                </div>
              </div>
            )}
          </section>

          <section className="mb-8">
            <SectionHeader number="02" title="Volume & composition" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <RankedTable
                title="By signal type"
                labelHeader="Signal type"
                rows={summary?.signal_types || []}
                getKey={(r) => r.signal_type}
                getLabel={(r) => r.signal_type}
                getValue={(r) => r.thread_count}
                totalThreads={totalThreads}
              />
              <RankedTable
                title="By topic"
                labelHeader="Topic"
                rows={summary?.topics || []}
                getKey={(r) => r.topic}
                getLabel={(r) => r.topic}
                getValue={(r) => r.thread_count}
                totalThreads={totalThreads}
              />
              <LifecycleCellTable rows={summary?.lifecycle_cells || []} totalThreads={totalThreads} />
              <RankedTable
                title="By actionability"
                labelHeader="Actionability"
                rows={summary?.actionability || []}
                getKey={(r) => r.actionability}
                getLabel={(r) => r.actionability}
                getValue={(r) => r.thread_count}
                totalThreads={totalThreads}
              />
            </div>
          </section>

          <section>
            <SectionHeader number="03" title="Pattern housekeeping" />
            <Card>
              <CardContent className="p-4">
                <div className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-ink-dim mb-3">
                  These patterns haven't been manually reviewed yet — their label may still change if a
                  future merge pass folds them into a similar pattern.
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pattern</TableHead>
                      <TableHead>Created at</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedHousekeeping.length === 0 ? (
                      <TableRow><TableCell colSpan={2} className="text-center py-6 text-ink-dim">Nothing pending review.</TableCell></TableRow>
                    ) : pagedHousekeeping.map((row) => (
                      <TableRow key={row.pattern}>
                        <TableCell>{row.pattern}</TableCell>
                        <TableCell className="text-xs text-ink-dim">{formatDate(row.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <PaginationFooter
                  total={housekeeping.length} page={housekeepingPage}
                  totalPages={housekeepingTotalPages} onPageChange={setHousekeepingPage}
                />
              </CardContent>
            </Card>
          </section>
        </>
      )}
    </div>
  );
};

// Remounts the page whenever the brand switches. Everything on it belongs to one brand — the
// date bounds, the option lists, every selected filter — so just refetching would keep the
// previous brand's selections (an agent or a pattern that doesn't exist there). The X-Brand
// header is already switched by the time the new page fetches: App's BrandSync renders ahead of
// the routes, so its effect runs first.
function LivechatMiningPageForBrand() {
  const { activeBrand } = useBrand();
  return <LivechatMiningPage key={activeBrand} />;
}

export default LivechatMiningPageForBrand;
